// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;
const NOTIFICATION_PREFS_QUERY_KEY = ['notification-preferences'] as const;

/** Hook for fetching user notifications. Refetches on window focus. */
export function useNotifications() {
  return useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/v1/notifications');

      if (error) {
        throw new Error(error.error.message);
      }
      // openapi-fetch types data and error as independently optional, so the
      // check above does not narrow data on its own.
      if (!data) {
        throw new Error('Request succeeded but returned no data');
      }

      return data.data;
    },
    refetchOnWindowFocus: true,
    staleTime: 60_000, // 1 minute
  });
}

/** Hook for fetching notification preferences. */
export function useNotificationPreferences() {
  return useQuery({
    queryKey: NOTIFICATION_PREFS_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        '/api/v1/settings/notification-preferences',
      );

      if (error) {
        throw new Error(error.error.message);
      }
      // openapi-fetch types data and error as independently optional, so the
      // check above does not narrow data on its own.
      if (!data) {
        throw new Error('Request succeeded but returned no data');
      }

      return data.data;
    },
    staleTime: 60_000,
  });
}

/** Hook for updating notification preferences. */
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (prefs: {
      cv_generation: boolean;
      low_credit: boolean;
    }) => {
      const { data, error } = await apiClient.PUT(
        '/api/v1/settings/notification-preferences',
        { body: prefs },
      );

      if (error) {
        throw new Error(error.error.message);
      }
      // openapi-fetch types data and error as independently optional, so the
      // check above does not narrow data on its own.
      if (!data) {
        throw new Error('Request succeeded but returned no data');
      }

      return data.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(NOTIFICATION_PREFS_QUERY_KEY, data);
    },
  });
}

/** Hook for dismissing a notification. Optimistically removes it from the list. */
export function useDismissNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/notifications/{id}/dismiss',
        { params: { path: { id } } },
      );

      if (error) {
        throw new Error(error.error.message);
      }
      // openapi-fetch types data and error as independently optional, so the
      // check above does not narrow data on its own.
      if (!data) {
        throw new Error('Request succeeded but returned no data');
      }

      return data.data;
    },
    onMutate: async (id) => {
      // Optimistic update: remove the notification immediately.
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });

      const previous = queryClient.getQueryData(NOTIFICATIONS_QUERY_KEY);

      queryClient.setQueryData(
        NOTIFICATIONS_QUERY_KEY,
        (old: { items: Array<{ id: string }>; unread_count: number } | undefined) => {
          if (!old) return old;
          const items = old.items.filter((item) => item.id !== id);
          return { items, unread_count: items.length };
        },
      );

      return { previous };
    },
    onError: (_err, _id, context) => {
      // Rollback on error.
      if (context?.previous) {
        queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    },
  });
}
